import { generateEmailHTML, fetchVendorEmailBrandingByCreatorUserId } from "./emailTemplate";
import { resolveEmailTemplateBrandingUserId } from "./authCookies";
import { sendEmail, type EmailPayload } from "./sendEmail";

/** Base64url-decode JWT payload (browser). */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  if (!token || typeof token !== "string") return null;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64.length % 4;
    if (pad) b64 += "=".repeat(4 - pad);
    if (typeof atob === "undefined") return null;
    return JSON.parse(atob(b64)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** User id for marketplace `created-by` when cookie is missing (e.g. cookie domain edge cases). */
function tryUserIdFromSessionJwt(token: string): string {
  const p = decodeJwtPayload(token);
  if (!p) return "";
  const u =
    p.userId ??
    p.user_id ??
    p.sub ??
    p.id ??
    (typeof p.user === "object" && p.user !== null && (p.user as { id?: string }).id);
  const s = u != null ? String(u).trim() : "";
  return s;
}

/**
 * Resolve `:userId` for `GET .../public/email-templates/users/:id`.
 * Order: explicit params → license rule ({@link resolveEmailTemplateBrandingUserId}) → JWT → localStorage userId.
 */
function resolveEmailTemplateUserIdForSend(
  jwtToken: string,
  params: Record<string, string>
): { id: string; source: string } {
  const fromParams =
    params.email_marketplace_created_by?.trim() ||
    params.marketplace_profile_created_by?.trim();
  if (fromParams) return { id: fromParams, source: "params" };

  const fromRule = resolveEmailTemplateBrandingUserId("");
  if (fromRule) return { id: fromRule, source: "license_type_cookie_rule" };

  const fromJwt = tryUserIdFromSessionJwt(jwtToken);
  if (fromJwt) return { id: fromJwt, source: "jwt_payload" };

  if (typeof localStorage !== "undefined") {
    const ls = String(localStorage.getItem("userId") || "").trim();
    if (ls) return { id: ls, source: "localStorage(userId)" };
  }

  return { id: "", source: "none" };
}

/**
 * License type configuration mapping
 * Maps license types to their corresponding environment variable names
 */
const LICENSE_TYPE_ENV_MAP: Record<string, string> = {
  SIGNAGE_FIRM: "REACT_APP_ENABLE_EMAIL_SIGNAGE_FIRM",
  SIGNAGE_TEAM_MEMBER: "REACT_APP_ENABLE_EMAIL_SIGNAGE_TEAM_MEMBER",
  AGENT: "REACT_APP_ENABLE_EMAIL_AGENT",
  BROKERAGE_FIRM: "REACT_APP_ENABLE_EMAIL_BROKERAGE_FIRM",
  BROKERAGE_TEAM_MEMBER: "REACT_APP_ENABLE_EMAIL_BROKERAGE_TEAM_MEMBER",
  BROKERAGE_AGENT: "REACT_APP_ENABLE_EMAIL_BROKERAGE_AGENT",
};

/**
 * Helper function to check if a license type is enabled for email triggers
 * @param licenseType - The license type to check (e.g., "SIGNAGE_FIRM", "AGENT")
 * @returns true if enabled (default), false if disabled via env variable
 */
function isLicenseTypeEnabled(licenseType: string): boolean {
  // Master switch: if REACT_APP_ENABLE_ALL_EMAIL is false, disable all emails
  const allEmailEnabled = process.env.REACT_APP_ENABLE_ALL_EMAIL;
  if (allEmailEnabled !== undefined && allEmailEnabled !== null) {
    const normalizedAllEmail = allEmailEnabled.trim().toLowerCase();
    if (normalizedAllEmail === "false" || normalizedAllEmail === "0" || normalizedAllEmail === "no") {
      return false;
    }
  }
  
  const envVarName = LICENSE_TYPE_ENV_MAP[licenseType];
  
  // If no mapping exists, default to enabled (backward compatibility)
  if (!envVarName) {
    return true;
  }
  
  // Get the environment variable value
  const envValue = process.env[envVarName];
  
  // If not set, default to enabled (backward compatibility)
  if (envValue === undefined || envValue === null) {
    return true;
  }
  
  // Parse the value - accept "true", "1", "yes" (case-insensitive) as enabled
  const normalizedValue = envValue.trim().toLowerCase();
  return normalizedValue === "true" || normalizedValue === "1" || normalizedValue === "yes";
}

/**
 * For a given triggerName, an array of licenseTypes and a parallel
 * array of recipients (same length), send one email per pair.
 * 
 * License type filtering: Only sends emails for license types that are enabled
 * via environment variables. If an env variable is not set, defaults to enabled.
 */
export async function sendTriggerEmails(
  triggerName: string,
  licenseTypes: string | string[],
  params: Record<string,string>,
  recipients: string | string[],
  from: string,
  jwt: string,
  attachments?: Record<string,string>
): Promise<void> {
  // Master switch: if REACT_APP_ENABLE_ALL_EMAIL is false, disable all emails
  const allEmailEnabled = process.env.REACT_APP_ENABLE_ALL_EMAIL;
  if (allEmailEnabled !== undefined && allEmailEnabled !== null) {
    const normalizedAllEmail = allEmailEnabled.trim().toLowerCase();
    if (normalizedAllEmail === "false" || normalizedAllEmail === "0" || normalizedAllEmail === "no") {
      console.log(`⚠️ Email trigger "${triggerName}" skipped - master switch REACT_APP_ENABLE_ALL_EMAIL is disabled`);
      return;
    }
  }

  // 1) normalize both to arrays (`to` addresses are sanitized in sendEmail — strips `email||role`)
  const types = Array.isArray(licenseTypes) ? licenseTypes : [licenseTypes];
  const recs = Array.isArray(recipients) ? recipients : [recipients];

  // 2) Filter license types based on environment configuration
  const enabledTypes: string[] = [];
  const enabledRecs: string[] = [];
  
  types.forEach((licenseType, index) => {
    if (isLicenseTypeEnabled(licenseType)) {
      enabledTypes.push(licenseType);
      // Maintain one-to-one correspondence if lengths match
      if (types.length === recs.length) {
        enabledRecs.push(recs[index]);
      }
    } else {
      console.log(`⚠️ Email trigger "${triggerName}" skipped for license type "${licenseType}" (disabled via env variable)`);
    }
  });
  
  // If no license types are enabled, skip sending emails
  if (enabledTypes.length === 0) {
    console.log(`⚠️ Email trigger "${triggerName}" skipped - all license types are disabled`);
    return;
  }
  
  // If lengths matched before, use filtered arrays. Otherwise, use all recipients for enabled types
  const filteredRecs = types.length === recs.length ? enabledRecs : recs;

  // 3) if lengths match, treat as one-to-one. Otherwise broadcast all.
  const pairs: Array<[string,string[]]> =
    enabledTypes.length === filteredRecs.length
      ? enabledTypes.map((lt, i) => [lt, [filteredRecs[i]]])
      : enabledTypes.map(lt => [lt, filteredRecs]);

  // 3b) Header/footer from public email template by user id (params → SIGNAGE_FIRM userId / else supplier → JWT fallback)
  const { id: emailTemplateUserId, source: emailTemplateIdSource } = resolveEmailTemplateUserIdForSend(jwt, params);

  if (emailTemplateUserId) {
    console.log("[emailService] Email template branding fetch:", {
      trigger: triggerName,
      userId: emailTemplateUserId,
      source: emailTemplateIdSource,
    });
  } else {
    console.warn(
      `[emailService] No user id for email template branding — trigger "${triggerName}". ` +
        "Expected params.email_marketplace_created_by, SIGNAGE_FIRM cookie userId, supplier id cookies/storage, JWT userId, or localStorage userId."
    );
  }

  const branding = emailTemplateUserId
    ? await fetchVendorEmailBrandingByCreatorUserId(emailTemplateUserId, jwt)
    : undefined;

  if (branding && (branding.headerLogoUrl || branding.socialLinks || branding.footerCustomerMessage)) {
    console.log("[emailService] Branding applied:", {
      hasHeaderLogo: !!branding.headerLogoUrl,
      socialLinkCount: branding.socialLinks ? Object.keys(branding.socialLinks).length : 0,
      hasFooterMessage: !!branding.footerCustomerMessage,
    });
  }

  // 4) build payloads
  const payloads: EmailPayload[] = pairs.map(([lt, toList]) => {
    const { subject, html, inlineAttachments } = generateEmailHTML(triggerName, lt, params, branding);
    return {
      to:          toList,
      from,
      subject,
      body:        html,
      attachments: attachments ?? {},
      inlineImageAttachments: inlineAttachments,
    };
  });

  // 5) send in parallel, returning the Promises
  const results = await Promise.allSettled(
    payloads.map(p => {
      console.log("→ sending", p.subject, "to", p.to);
      return sendEmail(p, jwt);
    })
  );

  // 6) aggregate failures (log only — do not throw so callers e.g. payment flows still complete)
  const errors = results
    .map((r, i) => ({ r, i }))
    .filter(x => x.r.status === "rejected")
    .map(x => `type=${pairs[x.i][0]} => ${(x.r as PromiseRejectedResult).reason}`);

  if (errors.length) {
    console.error(
      `[emailService] ${errors.length} of ${pairs.length} trigger sends failed for "${triggerName}":`,
      errors
    );
  }
}


// export async function sendTriggerEmails(
//   triggerName: string,
//   licenseTypes: string | string[],
//   params: Record<string,string>,
//   to: string[],
//   from: string,
//   jwt: string,
//   attachments?: Record<string,string>
// ): Promise<void> {
//   // normalize to array
//   const types = Array.isArray(licenseTypes) ? licenseTypes : [licenseTypes];

//   // build one payload per licenseType
//   const payloads: EmailPayload[] = types.map(lt => {
//     const { subject, html } = generateEmailHTML(triggerName, lt, params);
//     return { to, from, subject, body: html, attachments };
//   });

//   // fire them in parallel, but collect results
//   console.log("payload to send the sendEmails", payloads);
//   const results = await Promise.allSettled(
//     payloads.map(p => {
//         console.log("payload to send the sendEmails in parallel", p);
//         sendEmail(p, jwt)
//     })
//   );

//   // if any failed, throw a combined error
//   const errors = results
//     .map((r, i) => ({ r, i }))
//     .filter(x => x.r.status === "rejected")
//     .map(x => `type=${types[x.i]}: ${(x.r as PromiseRejectedResult).reason}`);

//   if (errors.length) {
//     console.error("Failed to send some emails:", errors);
//     throw new Error(`${errors.length} of ${types.length} emails failed.`);
//   }
// }
